<script setup>
import {reactive,onMounted} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";

const props = defineProps({
  contextSchemaA: String,
  contextIdA: String
});

const data = reactive({
  linkRequiredDocumentForm: false,
  dataLoading: true,
  expirationDate: '',
  isUseExpirationDate: false,
  loading: false,
  files: [],
  headers: [
    { title: 'Document Name', key: 'documentName' },
    { title: 'Linked Document', key: 'linkedFilename' },
    { title: 'Expiration Date', key: 'expirationDate' },
    { title: 'Actions', key: 'actions', sortable: false }
  ],
  items: [],
  fileToLink: {},
  currentItem: {
    requiredDocumentId: 0,
    fileId: 0,
    documentName: '',
    linkedFilename: '',
    expirationDate: ''
  }
});

const downloadFile = (file) => {
  if (file.linkedFilename !== null) {
    ApiWrapper.post('files', 'download', {
      key: file.key
    })
    .then((response) => {
      window.open(response.url, "_blank");
    })
    .catch((error) => {
      ToastUtil.error(error);
    });
  }
}

const formatISODateToAmerican = (date) => {
  if (!date) return null
  const [year, month, day] = date.split('-')
  return `${month}/${day}/${year}`
}

const listAll = () => {
  
  data.dataLoading = true;
  ApiWrapper.post('user-card', 'required-documents', {
    method: "loadAllData",
    personId: props.contextIdA
  })
  .then((response) => {
    if(response.files){
      data.files = response.files;
    }
    if(response.requiredDocuments){
      data.items = response.requiredDocuments;
    }
  })
  .catch((e)=>{
    ToastUtil.error(e);
  })
  .finally(()=>{
    data.dataLoading = false;
  });
}

const openLinkRequiredDocumentForm = (currentItem) => {
  data.currentItem.requiredDocumentId = currentItem.requiredDocumentId;
  data.fileId = 0;
  data.documentName = currentItem.documentName;
  data.linkedFilename = currentItem.linkedFilename;
  data.expirationDate = currentItem.expirationDate;
  if (data.expirationDate) {
    data.isUseExpirationDate = true;
  }
  data.linkRequiredDocumentForm = true;
}

const setFileLink = () => {
  ApiWrapper.post('user-card', 'required-documents', {
    method: 'setFileLink',
    personId: props.contextIdA,
    fileId: data.fileToLink.id,
    requiredDocumentId: data.currentItem.requiredDocumentId,
    metadata: {
      expirationDate: data.expirationDate,
      personId: props.contextIdA
    }
  })
  .then((response) => {
    if(response.files){
      data.files = response.files;
    }
    if(response.requiredDocuments){
      data.items = response.requiredDocuments;
    }
  })
  .catch((e)=>{
    ToastUtil.error(e);
  })
  .finally(()=>{
    data.dataLoading = false;
    data.linkRequiredDocumentForm = false;
  });
}


onMounted(()=>{
  listAll();
})
</script>

<template>
  <v-container fluid>
    <v-card class="elevation-2" height="auto" width="auto">
      <v-toolbar flat color="white">
        <v-toolbar-title>Required Documents</v-toolbar-title>
        <v-progress-linear :active="data.loading" :indeterminate="data.loading" absolute bottom color="primary">
        </v-progress-linear>
        <v-divider class="mx-4" inset vertical>
        </v-divider>
      </v-toolbar>
      <div v-if="data.linkRequiredDocumentForm">
        <v-card-title class="primary--text">
          Link Required Document to File
        </v-card-title>
        <v-row class="mx-1">
          <v-col cols="12">
            <v-checkbox label="Use Expiration Date"
                        v-model="data.isUseExpirationDate">
            </v-checkbox>
          </v-col>
          <v-col v-if="data.isUseExpirationDate">
            <v-date-picker v-model="data.expirationDate">
            </v-date-picker>
          </v-col>
          <v-col cols="12">
            <v-list flat>
              <v-list-item multiple>
                <template v-for="(file, index) in data.files" :key="file.fileid">
                  <v-list-item >
                    <template v-slot:default="{}">
                      <v-list-item @click="data.fileToLink = file">
                        <v-list-item-title
                        >{{file.filename}}</v-list-item-title>
                      </v-list-item>
                      <v-list-item-action v-if="data.fileToLink === file">
                        <v-btn size="small" variant="elevated" class="primary" @click="setFileLink()">
                          Submit
                        </v-btn>
                      </v-list-item-action>
                    </template>
                  </v-list-item>
                  <v-divider
                      v-if="index + 1 < data.files.length"
                      :key="index"
                  ></v-divider>
                </template>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </div>
      <v-data-table :headers="data.headers"
                    :items="data.items"
                    class="elevation-1"
                    :loading="data.dataLoading"
                    loading-text="Loading... Please wait">
        <template v-slot:[`data.items.linkedFilename`] >
          {{ data.items.value ? data.items.value : 'Not Present' }}
        </template>
        <template v-slot:[`data.items.expirationDate`]>
          {{ data.items.value ? formatISODateToAmerican(data.items.value) : 'No Expiration Date' }}
        </template>
        <template v-slot:[`data.items.actions`]="{ item }">
          <v-icon class="mr-2" @click="openLinkRequiredDocumentForm(item)">
            mdi-link-variant
          </v-icon>
          <v-icon v-show="item.linkedFilename !== undefined" v-if="Permissions.checkPermissions(['files.download'])" class="mr-2" @click="downloadFile(item)">
            mdi-download
          </v-icon>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.container {
  padding: 5px;
}

[v-cloak] {
  display: none;
}
</style>